import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { formatDateDDMMYYYY } from "../utils/dateUtils";
import { convertTo12HourFormat } from "../utils/timeUtils";
import {
  FaCreditCard,
  FaQrcode,
  FaUniversity,
  FaWallet,
  FaTag,
  FaTimes,
} from "react-icons/fa";
import {
  PaymentSuccessAnimation,
  BookingConfirmedAnimation,
  AnimationStyles,
} from "../components/PaymentAnimations";

// Utility function to safely decode time parameters
const decodeTimeParam = (timeParam) => {
  if (!timeParam) return null;
  try {
    return decodeURIComponent(timeParam);
  } catch (e) {
    return timeParam; // Return original if decoding fails
  }
};

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [bookingInfo, setBookingInfo] = useState({});
  const [serviceType, setServiceType] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState("razorpay");
  const [promoCode, setPromoCode] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoApplied, setPromoApplied] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [packageName, setPackageName] = useState(null);

  // Animation states
  const [showPaymentSuccessAnimation, setShowPaymentSuccessAnimation] =
    useState(false);
  const [showBookingConfirmedAnimation, setShowBookingConfirmedAnimation] =
    useState(false);

  // Extract query parameters
  const queryParams = new URLSearchParams(location.search);

  // API URL
  // Import API URL from config
  const { API_URL } = require("../config/apiConfig");

  // Get auth state directly from Redux and localStorage for complete coverage
  const authState = useSelector((state) => state.auth || {});

  // Get auth from cookie and localStorage as backup method
  let storedToken = null;
  try {
    const cookieToken = document.cookie
      .split(";")
      .find((c) => c.trim().startsWith("token="));
    if (cookieToken) {
      storedToken = cookieToken.split("=")[1];
    }
  } catch (e) {}

  // Try to find authentication token from all possible sources
  // Looking at your auth format, the token is in the success response from login
  const token =
    authState.token ||
    (authState.user && authState.user.token) ||
    storedToken ||
    localStorage.getItem("token") ||
    null;

  // Manually check localStorage for auth
  const storedAuth = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : null;

  // Combine Redux and localStorage sources
  const combinedAuthData = {
    ...authState,
    ...(storedAuth || {}),
  };

  // Get user from the combined auth data
  const user = combinedAuthData.user || authState.user || {};


  // Extract user ID from all possible locations based on how your auth system works
  const userId =
    (user && user.id) ||
    (user && user._id) ||
    (storedAuth &&
      storedAuth.user &&
      (storedAuth.user.id || storedAuth.user._id)) ||
    "645f9d9ee88ebd3ddd7ac4c7"; // Fallback MongoDB ID

  // Set up authorization headers with the JWT token from your auth system
  // This format must match what your auth.js middleware expects
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
    },
  };


  // Store default MongoDB ObjectIds for required collections (for testing only)
  const DEFAULT_CAB_ID = "645f9d9ee88ebd3ddd7ac4c8";
  const DEFAULT_CITY_ID = "645f9d9ee88ebd3ddd7ac4c9";

  // Handle authentication and login redirect with maximum compatibility
  useEffect(() => {
    // Always save current URL params for post-login redirect
    const paymentParams = location.search;
    if (paymentParams) {
      sessionStorage.setItem("paymentRedirectParams", paymentParams);
      localStorage.setItem("paymentRedirectParams", paymentParams); // Backup in localStorage too
    }

    // Check if we just came back from login (check URL for redirect=payment)
    const justLoggedIn = location.search.includes("login-success=true");

    // Try extracting token from localStorage if Redux failed
    if (!token && storedAuth && storedAuth.token) {
      // Don't redirect - we found authentication in localStorage
      return;
    }

    // Comprehensive authentication check using all sources
    const isAuthenticated =
      !!token ||
      !!(user && (user._id || user.id)) ||
      !!(storedAuth && storedAuth.token);


    // Special case - just logged in but still missing token
    if (justLoggedIn) {
      return;
    }

    // Only redirect if definitely not authenticated
    if (!isAuthenticated && !authState.loading) {
      toast.warning("Please log in to continue booking");
      // Add timestamp to prevent caching issues
      navigate(`/login?redirect=payment&ts=${Date.now()}`);
    } else {
    }
  }, []);

  // We don't add dependencies to avoid re-running this effect and creating redirect loops

  // Get cab details from localStorage or use static data
  const getCabDetails = (
    cabId,
    fromCity,
    toCity,
    bookingType,
    name,
    mobile,
    email,
    pickupAddress,
    dropAddress,
    amount,
    travelDate,
    travelTime,
    distance,
    serviceType,
  ) => {
    setIsLoading(true);

    // First try to get from localStorage (set in CarSelection page)
    const savedCabData = localStorage.getItem("selectedCabData");
    let cabDetails = null;

    if (savedCabData) {
      try {
        const parsedData = JSON.parse(savedCabData);
        if (parsedData.id === cabId) {
          cabDetails = parsedData;
        }
      } catch (parseError) {
        console.error("Error parsing saved cab data:", parseError);
      }
    }

    // If we can't get from localStorage, look up in static data or use URL params
    if (!cabDetails) {
      // Static cab data
      const staticCabs = {
        cab1: {
          _id: "cab1",
          name: "Economy Hatchback",
          basePrice: 1800,
          imageUrl:
            "https://images.unsplash.com/photo-1541899481282-d53bffe3c35d?w=500&h=300",
        },
        cab2: {
          _id: "cab2",
          name: "Comfort Sedan",
          basePrice: 2200,
          imageUrl:
            "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=500&h=300",
        },
        cab3: {
          _id: "cab3",
          name: "Premium SUV",
          basePrice: 3500,
          imageUrl:
            "https://images.unsplash.com/photo-1565043589221-2572feea2d2c?w=500&h=300",
        },
        cab4: {
          _id: "cab4",
          name: "Luxury Sedan",
          basePrice: 4500,
          imageUrl:
            "https://images.unsplash.com/photo-1553440569-bcc63803a83d?w=500&h=300",
        },
      };

      if (staticCabs[cabId]) {
        cabDetails = staticCabs[cabId];
      } else {
        const cabName = queryParams.get("cabName");
        cabDetails = {
          _id: cabId,
          name: cabName || "Selected Cab",
          basePrice: parseInt(amount) || 3000,
          imageUrl:
            "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=300&h=200",
        };
      }
    }

    // Set booking info with proper pricing calculation
    let basePrice, taxAmount, totalAmount;
    const originalAmount = parseInt(amount) || cabDetails.basePrice;

    // For airport services, the incoming amount is the base price (before tax)
    // For other services, it might be the base price
    if (serviceType === "airport") {
      basePrice = originalAmount;
      taxAmount = Math.round(basePrice * 0.18);
      // Ensure minimum tax of ₹1 for any non-zero base price
      if (taxAmount === 0 && basePrice > 0) {
        taxAmount = 1;
      }
      totalAmount = basePrice + taxAmount;
    } else {
      // Treat the incoming amount as base price (actual price)
      basePrice = originalAmount;
      taxAmount = Math.round(basePrice * 0.18);
      // Ensure minimum tax of ₹1 for any non-zero base price
      if (taxAmount === 0 && basePrice > 0) {
        taxAmount = 1;
      }
      totalAmount = basePrice + taxAmount;
    }


    setBookingInfo({
      fromCity,
      toCity,
      cabId,
      cabName: cabDetails.name,
      cabImage: cabDetails.imageUrl || "",
      bookingType,
      name,
      mobile,
      email,
      pickupAddress,
      dropAddress,
      baseAmount: basePrice,
      amount: basePrice, // This is the actual base price (shown as "Actual Price")
      taxAmount: taxAmount, // Calculated tax amount
      totalAmount: totalAmount, // Total amount including tax
      travelDate,
      travelTime: decodeTimeParam(travelTime),
      pickupTime: decodeTimeParam(travelTime),
      distance: parseFloat(distance) || 0,
    });

    setIsLoading(false);
  };

  // Initialize booking info from URL parameters
  useEffect(() => {
    const fromCity = queryParams.get("from");
    const toCity = queryParams.get("to");
    const cabId = queryParams.get("cabId");
    const bookingType = queryParams.get("tripType") || queryParams.get("type");
    const name = queryParams.get("name");
    const mobile = queryParams.get("mobile");
    const email = queryParams.get("email");
    const pickupAddress = queryParams.get("pickupAddress")
      ? decodeURIComponent(queryParams.get("pickupAddress"))
      : "";
    const dropAddress = queryParams.get("dropAddress")
      ? decodeURIComponent(queryParams.get("dropAddress"))
      : "";
    const amount = queryParams.get("amount");
    const travelDate = queryParams.get("date");
    const travelTime = queryParams.get("time");
    const distance = queryParams.get("distance");
    const serviceTypeParam = queryParams.get("service"); // rental, airport, or null for outstation

    // Handle duplicate package parameters by finding the best decoded version
    const packageNameFromUrl = (() => {
      const allPackageParams = queryParams.getAll("package");
      if (allPackageParams.length === 0) return null;

      // Try to find the cleanest version (shortest after decoding usually means less encoding)
      let bestPackage = allPackageParams[0];
      for (const pkg of allPackageParams) {
        try {
          const decoded = decodeURIComponent(pkg);
          const currentBestDecoded = decodeURIComponent(bestPackage);
          // Choose the version that looks more natural (contains spaces and forward slashes)
          if (
            decoded.includes(" ") &&
            decoded.includes("/") &&
            decoded.length < currentBestDecoded.length
          ) {
            bestPackage = pkg;
          }
        } catch (e) {
          // Skip malformed packages
        }
      }
      return bestPackage;
    })();

    // Set package name in state
    setPackageName(packageNameFromUrl);

    const cityName = queryParams.get("city"); // rental city
    setServiceType(serviceTypeParam);


    // Validate required parameters based on service type
    const isRentalOrAirport =
      serviceTypeParam === "rental" || serviceTypeParam === "airport";

    if (
      !fromCity ||
      (!toCity && !isRentalOrAirport) || // toCity not required for rental/airport services
      !cabId ||
      !name ||
      !mobile ||
      (!email && !isRentalOrAirport) || // email not mandatory for rental/airport services
      !amount
    ) {
      toast.error("Missing required booking information");
      navigate("/");
      return;
    }

    // Call the function once on initial render
    getCabDetails(
      cabId,
      fromCity,
      toCity || fromCity, // For rental/airport services, use fromCity as fallback
      bookingType,
      name,
      mobile,
      email,
      pickupAddress,
      dropAddress,
      amount,
      travelDate,
      travelTime,
      distance,
      serviceTypeParam,
    );

    // Load Razorpay script
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []); // Empty dependency array means this effect runs once on mount

  // Fetch available coupons on component mount
  useEffect(() => {
    fetchAvailableCoupons();
  }, []);

  // Fetch available coupons from admin panel
  const fetchAvailableCoupons = async () => {
    try {
      // Try to fetch active coupons - no authentication required for public endpoint
      const response = await axios.get(`${API_URL}/coupons/active`);
      if (response.data.success) {
        setAvailableCoupons(response.data.coupons || []);
      } else {
        throw new Error('No active coupons found');
      }
    } catch (error) {
      console.error("Error fetching coupons:", error);
      // Fallback to default coupons that match admin panel setup
      setAvailableCoupons([
        { code: "WELCOME10", discountType: "percentage", discountValue: 10, description: "Welcome discount" },
        { code: "FIRST20", discountType: "percentage", discountValue: 20, description: "First booking discount" },
        { code: "SPECIAL15", discountType: "percentage", discountValue: 15, description: "Special offer" },
      ]);
    }
  };

  // Apply promo code
  const applyPromoCode = () => {
    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }

    if (promoApplied) {
      toast.warning("Promo code already applied");
      return;
    }

    // Find the coupon in available coupons
    const coupon = availableCoupons.find(c => c.code === promoCode.toUpperCase());

    if (coupon) {
      let discountAmount = 0;

      if (coupon.discountType === "percentage") {
        discountAmount = Math.round((bookingInfo.baseAmount * coupon.discountValue) / 100);

        // Apply maximum discount limit if specified
        if (coupon.maximumDiscountAmount) {
          discountAmount = Math.min(discountAmount, coupon.maximumDiscountAmount);
        }
      } else {
        // Fixed amount discount
        discountAmount = coupon.discountValue;
      }

      // Check minimum booking amount
      if (coupon.minimumBookingAmount && bookingInfo.totalAmount < coupon.minimumBookingAmount) {
        toast.error(`Minimum booking amount of ₹${coupon.minimumBookingAmount} required for this coupon`);
        return;
      }

      // Apply discount first, then calculate tax on discounted amount
      const discountedAmount = Math.max(0, bookingInfo.baseAmount - discountAmount);
      const newTaxAmount = Math.round(discountedAmount * 0.18);
      // Ensure minimum tax of ₹1 for any non-zero discounted amount
      const finalTaxAmount = (newTaxAmount === 0 && discountedAmount > 0) ? 1 : newTaxAmount;
      const newTotalAmount = discountedAmount + finalTaxAmount;

      setPromoDiscount(discountAmount);
      setPromoApplied(true);

      // Update booking info with new calculations
      setBookingInfo({
        ...bookingInfo,
        taxAmount: finalTaxAmount,
        totalAmount: newTotalAmount,
      });

      const discountText = coupon.discountType === "percentage"
        ? `${coupon.discountValue}% discount`
        : `₹${coupon.discountValue} discount`;
      toast.success(`${discountText} applied successfully!`);
    } else {
      toast.error("Invalid promo code");
    }
  };

  // Remove promo code
  const removePromoCode = () => {
    setPromoCode("");
    setPromoDiscount(0);
    setPromoApplied(false);

    // Reset tax calculation to original base amount
    const originalTaxAmount = Math.round(bookingInfo.baseAmount * 0.18);
    const finalOriginalTaxAmount = (originalTaxAmount === 0 && bookingInfo.baseAmount > 0) ? 1 : originalTaxAmount;

    // Reset total amount
    setBookingInfo({
      ...bookingInfo,
      taxAmount: finalOriginalTaxAmount,
      totalAmount: bookingInfo.baseAmount + finalOriginalTaxAmount,
    });
  };

  // Create a booking and initiate payment
  const initiatePayment = async () => {
    try {
      setProcessingPayment(true);

      // Generate a temporary booking ID for demo purposes
      const tempBookingId = "UR" + Date.now().toString().substring(5);

      // Setup API config
      // Import API URL from config
      const { API_URL } = require("../config/apiConfig");
      let config = {
        headers: {
          "Content-Type": "application/json",
        },
      };

      // Add auth token if available
      const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
      if (userInfo && userInfo.token) {
        config.headers.Authorization = `Bearer ${userInfo.token}`;
      }

      // Prepare frontend booking data for localStorage
      const bookingData = {
        id: tempBookingId,
        from: bookingInfo.fromCity,
        to: bookingInfo.toCity,
        date: bookingInfo.travelDate || new Date().toISOString().split("T")[0],
        time: bookingInfo.travelTime || bookingInfo.pickupTime || "10:00 AM",
        cabName: bookingInfo.cabName,
        cabType: bookingInfo.cabType || "Sedan",
        amount: bookingInfo.totalAmount,
        baseAmount: bookingInfo.baseAmount,
        taxAmount: bookingInfo.totalAmount - bookingInfo.baseAmount,
        paymentMethod:
          selectedPaymentMethod === "razorpay"
            ? "ONLINE"
            : selectedPaymentMethod.toUpperCase(),
        passengerDetails: {
          name: bookingInfo.name,
          email: bookingInfo.email,
          phone: bookingInfo.mobile,
        },
      };

      // Step 1: Try to save booking to MongoDB database
      try {
        // Extract cab ID and use actual addresses instead of ObjectIDs for better display
        const cabID =
          queryParams.get("cabId") || bookingInfo.cabId || DEFAULT_CAB_ID;

        // Create location objects that match the Booking model structure
        const pickupLocationObj = {
          name: bookingInfo.fromCity || "Pickup City",
          address: queryParams.get("pickupAddress")
            ? decodeURIComponent(queryParams.get("pickupAddress"))
            : bookingInfo.fromCity || "Pickup Location",
        };

        const dropLocationObj = {
          name: bookingInfo.toCity || "Drop City",
          address: queryParams.get("dropAddress")
            ? decodeURIComponent(queryParams.get("dropAddress"))
            : bookingInfo.toCity || "Drop Location",
        };

        // Comprehensive authentication check using all authentication sources
        // This matches how we check in the initial useEffect
        const isAuthenticated =
          !!token ||
          !!(user && (user._id || user.id)) ||
          !!(storedAuth && storedAuth.token);

        if (!isAuthenticated) {
          toast.error("Authentication required - please log in again");
          setProcessingPayment(false);
          navigate("/login?redirect=payment&ts=" + Date.now());
          return;
        }

        // If we're missing a token but have user ID, try to proceed anyway
        // The backend will use the cookie token if available


        // We'll continue regardless as the user may be authenticated in various ways

        // Use the standard booking endpoint with authenticated user only
        // We now require a real user for all bookings
        const apiEndpoint = `${API_URL}/booking`;


        // Create a booking payload with real data instead of mock data
        const bookingPayload = {
          // Required MongoDB ObjectId reference for cab type
          cabType: cabID,
          // Use location objects that match the Booking model
          pickupLocation: pickupLocationObj,
          dropLocation: dropLocationObj,

          // Required MongoDB user reference - must be a valid ObjectId
          user: userId,

          // Ensure required status field is included for MongoDB validation
          status: "pending",

          // Include real passenger details from the booking form - no fallbacks
          passengerDetails: {
            name: bookingInfo.name,
            email: bookingInfo.email,
            phone: bookingInfo.mobile,
          },

          // Address fields using real data - ensure correct mapping
          pickupAddress:
            bookingInfo.pickupAddress || queryParams.get("pickupAddress")
              ? decodeURIComponent(queryParams.get("pickupAddress"))
              : "", // Detailed pickup address
          dropAddress:
            bookingInfo.dropAddress || queryParams.get("dropAddress")
              ? decodeURIComponent(queryParams.get("dropAddress"))
              : "", // Detailed drop address
          from: bookingInfo.fromCity, // City for pickup
          to: bookingInfo.toCity, // City for drop-off
          fromParam: queryParams.get("fromParam") || bookingInfo.fromCity, // Full address for pickup
          toParam: queryParams.get("toParam") || bookingInfo.toCity, // Full address for drop-off
          dbPickup:
            queryParams.get("dbPickup") ||
            (queryParams.get("pickupAddress")
              ? decodeURIComponent(queryParams.get("pickupAddress"))
              : ""), // Database pickup address
          dbDrop:
            queryParams.get("dbDrop") ||
            (queryParams.get("dropAddress")
              ? decodeURIComponent(queryParams.get("dropAddress"))
              : ""), // Database drop address
          dbFrom: queryParams.get("dbFrom") || bookingInfo.fromCity, // Database pickup city
          dbTo: queryParams.get("dbTo") || bookingInfo.toCity, // Database drop city
          pickupAddressCity:
            queryParams.get("pickupAddressCity") || bookingInfo.fromCity, // City-specific pickup address
          dropAddressCity:
            queryParams.get("dropAddressCity") || bookingInfo.toCity, // City-specific drop address

          // Via cities information if present
          viaCities: queryParams.get("via")
            ? queryParams.get("via").split(",").map(city => city.trim()).filter(city => city.length > 0)
            : [],

          // Amount fields - convert to numbers with explicit parsing, no default values
          distance: parseInt(bookingInfo.distance || 0, 10),
          duration: parseInt(bookingInfo.duration || 0, 10),
          baseAmount: parseInt(bookingInfo.baseAmount, 10),
          taxAmount: parseInt(bookingInfo.taxAmount, 10),
          totalAmount: parseInt(bookingInfo.totalAmount, 10),

          // Date and Time fields - ensure they're passed correctly
          pickupDate:
            bookingInfo.travelDate ||
            bookingInfo.pickupDate ||
            new Date().toISOString().split("T")[0],
          pickupTime:
            bookingInfo.travelTime || bookingInfo.pickupTime || "10:00",

          // Trip type information (One Way vs Round Trip vs Multi-City)
          // Note: Backend model only supports oneWay and roundTrip, multiCity is handled via viaCities
          journeyType: queryParams.get("tripType") === "roundTrip" ? "roundTrip" : "oneWay",

          // Return trip information for round trips
          ...(queryParams.get("tripType") === "roundTrip" && {
            returnDate: queryParams.get("returnDate") || null,
            returnTime: queryParams.get("returnTime") || null,
          }),

          // Status info
          status: "pending",
          paymentStatus: "pending",
          paymentMethod:
            selectedPaymentMethod === "razorpay"
              ? "ONLINE"
              : selectedPaymentMethod.toUpperCase(),
          bookingId: tempBookingId,

          // Coupon information if applied
          ...(promoApplied && promoCode && {
            couponCode: promoCode,
            couponDiscountAmount: promoDiscount,
            originalAmount: bookingInfo.baseAmount + bookingInfo.taxAmount,
          }),

          // Rental package information (if this is a rental service)
          ...(serviceType === "rental" &&
            queryParams.get("package") && {
              serviceType: "rental",
              packageName: decodeURIComponent(queryParams.get("package")),
              // Extract hours and km from package name format like "2 Hour / 20 Km"
              rentalHours: (() => {
                const pkgName = queryParams.get("package");
                const match = pkgName ? pkgName.match(/(\d+)\s*hour/i) : null;
                return match ? parseInt(match[1], 10) : null;
              })(),
              rentalKm: (() => {
                const pkgName = queryParams.get("package");
                const match = pkgName ? pkgName.match(/(\d+)\s*km/i) : null;
                return match ? parseInt(match[1], 10) : null;
              })(),
            }),

          // Add airport service data
          ...(serviceType === "airport" && {
            serviceType: "airport",
            airport: queryParams.get("airport"),
            airportCode: queryParams.get("airport"),
            transferDirection: queryParams.get("direction"),
            journeyType: "oneWay", // Temporarily using oneWay for airport services until backend updates
          }),
        };

        // We've already added the user ID from the authenticated user
        // No need for a condition since we require authentication


        // Attempt to save booking with improved error handling
        let bookingResponse;
        try {

          // Properly format headers with authentication if available
          // Ensure the Authorization header matches exactly what your middleware expects
          // Your auth.js middleware checks for Bearer token format
          const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          };

          // Add any available token to cookies for maximum compatibility
          // Many backends check cookies first, then headers
          const tokenToUse = token || (storedAuth && storedAuth.token) || "";
          if (tokenToUse) {
            document.cookie = `token=${tokenToUse}; path=/; max-age=86400`;
          }


          // Simplified booking approach with localStorage fallback
          try {
            // Make a standard API call with authentication
            bookingResponse = await axios.post(
              `${API_URL}/booking`,
              bookingPayload,
              {
                headers: headers,
              },
            );
          } catch (error) {

            // In case of API failure, store locally as a fallback

            // Generate a local ID to use temporarily
            const localBookingId = "local-" + Date.now();

            // Create a local booking version
            const localBooking = {
              ...bookingPayload,
              _id: localBookingId,
              localOnly: true,
              createdAt: new Date().toISOString(),
            };

            // Get existing bookings or create empty array
            const existingBookings = localStorage.getItem("bookings")
              ? JSON.parse(localStorage.getItem("bookings"))
              : [];

            // Add new booking to array and save
            existingBookings.push(localBooking);
            localStorage.setItem("bookings", JSON.stringify(existingBookings));

            // Create mock response
            bookingResponse = {
              data: {
                success: true,
                data: localBooking,
                message: "Booking stored locally (offline mode)",
              },
            };
          }

        } catch (dbError) {
          console.error("Error saving to database:", dbError.message);
          console.error("Error response:", dbError.response?.data);

          // Provide specific error messages
          if (dbError.response?.status === 401) {
            toast.error("Authentication error - check guest user setup");
          } else if (dbError.response?.status === 400) {
            toast.error("Invalid booking data - check required fields");
          } else {
            toast.warning("Server error - using fallback");
          }

          // If we can't connect to the database, we should stop the process
          alert("Cannot connect to the server. Please try again later.");
          setProcessingPayment(false);
          return; // Exit the function instead of proceeding with a fake response
        }

        // If the booking was saved successfully
        if (bookingResponse.data && bookingResponse.data.success) {
          console.log(
            "Booking successfully saved to MongoDB:",
            bookingResponse.data,
          );


          // Update booking data with database ID
          if (bookingResponse.data.data && bookingResponse.data.data._id) {
            const dbId = bookingResponse.data.data._id;
            bookingData.dbId = dbId;
            bookingData.mongoId = dbId;

            // Use the server-generated bookingId if available
            if (bookingResponse.data.data.bookingId) {
              bookingData.id = bookingResponse.data.data.bookingId;
            }
          }
        } else {
          toast.warning("Could not save to database.");
        }
      } catch (dbError) {
        console.error("Failed to save booking to database:", dbError);
        toast.error("Could not connect to database. Proceeding with payment.");
      }
      // End of saveBooking try/catch
      // For COD option, redirect directly to confirmation
      if (selectedPaymentMethod === "cod") {
        // Check if this is a local ID (starts with 'local_') or a real MongoDB ID
        const targetId = bookingData.dbId || bookingData.mongoId;
        const isLocalId = targetId && targetId.toString().startsWith("local_");

        if (targetId) {
          // Try to update payment method in the database
          try {
  
            // Determine if this is a custom/local ID or MongoDB ID
            const isCustomBookingId = targetId.toString().includes("local-") ||
                                     targetId.toString().startsWith("CB");
            // MongoDB ObjectIDs are 24-character hex strings - use standard endpoint for these

            let paymentUpdateResponse;

            if (isCustomBookingId) {
              // Use custom endpoint for local/custom IDs
              paymentUpdateResponse = await axios.post(
                `${API_URL}/booking/payment-update/custom`,
                {
                  bookingId: targetId,
                  paymentMethod: "COD",
                  paymentStatus: "pending",
                },
                {
                  headers: {
                    "Content-Type": "application/json",
                  },
                },
              );
            } else {
              // Use standard endpoint for MongoDB IDs

              // Get the auth token from Redux state or localStorage
              const token =
                authState.token ||
                (authState.user && authState.user.token) ||
                localStorage.getItem("token") ||
                (JSON.parse(localStorage.getItem("user")) || {}).token;

              if (!token) {
                console.error("No authentication token available");
                toast.error("Authentication required. Please log in again.");
                navigate("/login");
                return;
              }

              paymentUpdateResponse = await axios.put(
                `${API_URL}/booking/${targetId}/payment-method`,
                {
                  paymentMethod: "cod",
                  paymentStatus: "pending",
                  user: userId, // Add user ID for authentication fallback
                },
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                },
              );
            }

            if (
              paymentUpdateResponse.data &&
              paymentUpdateResponse.data.success
            ) {
              toast.success("Booking confirmed as Cash on Delivery");
            } else {
              toast.warning(
                "Could not update payment method, but booking is still confirmed",
              );
            }
          } catch (payError) {
            console.error("Error updating COD payment:", payError);
            if (payError.response && payError.response.status === 401) {
              toast.error("Session expired. Please log in again.");
              navigate("/login");
            } else {
              toast.warning(
                "Could not connect to server, but booking is still confirmed",
              );
            }
          }
        } else {
          // This is a local ID or no ID, just update MongoDB
          toast.info("Booking information saved to MongoDB");
        }

        // Redirect to booking confirmation page first to show animation
        const bookingId =
          bookingData.dbId || bookingData.mongoId || bookingData.id;

        // Include all the trip information in the URL for the confirmation page
        const confirmationParams = new URLSearchParams();
        confirmationParams.append("method", "cod");
        confirmationParams.append("bookingId", bookingId);
        confirmationParams.append("from", bookingInfo.fromCity);
        confirmationParams.append("to", bookingInfo.toCity);
        confirmationParams.append(
          "pickupAddress",
          encodeURIComponent(bookingInfo.pickupAddress || ""),
        );
        confirmationParams.append(
          "dropAddress",
          encodeURIComponent(bookingInfo.dropAddress || ""),
        );
        confirmationParams.append("cabName", bookingInfo.cabName);
        confirmationParams.append("amount", bookingInfo.totalAmount);
        confirmationParams.append("date", bookingInfo.pickupDate || "");
        confirmationParams.append("time", bookingInfo.pickupTime || "");
        confirmationParams.append("distance", bookingInfo.distance || "");

        // Add rental package parameters if this is a rental service
        if (serviceType === "rental") {
          confirmationParams.append("service", "rental");
          if (queryParams.get("package")) {
            confirmationParams.append(
              "packageName",
              queryParams.get("package"),
            );
          }
          if (queryParams.get("city")) {
            confirmationParams.append("city", queryParams.get("city"));
          }
        }

        // Show the booking confirmed animation with COD payment method first
        setShowPaymentSuccessAnimation(true);
        // Set selected payment method to ensure the correct message is shown
        const paymentMethodForAnimation = "cod";

        // Wait for animation to complete before redirecting
        setTimeout(() => {
          setShowPaymentSuccessAnimation(false);
          // Navigate to booking details page after successful booking

          // Build query parameters for booking details
          const bookingParams = new URLSearchParams();
          bookingParams.append("from", bookingInfo.fromCity || bookingInfo.pickupAddress || "");
          bookingParams.append("to", bookingInfo.toCity || bookingInfo.dropAddress || "");
          bookingParams.append("cabName", bookingInfo.cabType || bookingInfo.cabName || "");
          bookingParams.append("amount", bookingInfo.totalAmount || bookingInfo.amount || "");
          bookingParams.append("distance", bookingInfo.distance || "");

          navigate(`/booking/${bookingId}?${bookingParams.toString()}`);
        }, 3000);

        return;
      }

      // For Razorpay payment - use client-side only integration
      // Check if Razorpay script is loaded
      if (!window.Razorpay) {
        toast.error(
          "Razorpay SDK failed to load. Please refresh the page and try again.",
        );
        setProcessingPayment(false);
        return;
      }

      // Initialize Razorpay checkout with the most basic configuration
      const options = {
        key: "rzp_test_PnSgiqE2elzEYx", // Razorpay test key
        amount: bookingInfo.totalAmount * 100, // Amount in paise
        currency: "INR",
        name: "UrbanRide",
        description: `Booking from ${bookingInfo.fromCity} to ${bookingInfo.toCity}`,
        handler: async function (response) {
          setProcessingPayment(false);

          // First show the payment success animation
          setShowPaymentSuccessAnimation(true);

          // Extract the payment ID
          const paymentId = response.razorpay_payment_id;

          try {
            // Update booking with payment details first
            let updatedBooking = {
              ...bookingData,
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
              signature: response.razorpay_signature,
              paymentStatus: "paid",
            };

            // Get the booking ID for navigation - use the original bookingData as primary source
            const primaryBookingId =
              bookingData.dbId || bookingData.mongoId || bookingData.id;

            // Show booking confirmed animation, then redirect
            setShowBookingConfirmedAnimation(true);
            setTimeout(() => {
              setShowBookingConfirmedAnimation(false);

              // Navigate to booking confirmation page first
              const confirmationParams = new URLSearchParams();
              confirmationParams.append("method", "razorpay");
              confirmationParams.append("bookingId", primaryBookingId);
              confirmationParams.append("from", bookingInfo.fromCity);
              confirmationParams.append("to", bookingInfo.toCity);
              confirmationParams.append(
                "pickupAddress",
                bookingInfo.pickupAddress || "",
              );
              confirmationParams.append(
                "dropAddress",
                bookingInfo.dropAddress || "",
              );
              confirmationParams.append("cabName", bookingInfo.cabName);
              confirmationParams.append("amount", bookingInfo.totalAmount);
              confirmationParams.append(
                "date",
                bookingInfo.pickupDate || bookingInfo.travelDate || "",
              );
              confirmationParams.append(
                "time",
                bookingInfo.pickupTime || bookingInfo.travelTime || "",
              );
              confirmationParams.append("distance", bookingInfo.distance || "");
              confirmationParams.append(
                "paymentId",
                response.razorpay_payment_id,
              );
              confirmationParams.append("paymentStatus", "completed");


              // Build query parameters for booking details
              const bookingParams = new URLSearchParams();
              bookingParams.append("from", bookingInfo.fromCity || bookingInfo.pickupAddress || "");
              bookingParams.append("to", bookingInfo.toCity || bookingInfo.dropAddress || "");
              bookingParams.append("cabName", bookingInfo.cabType || bookingInfo.cabName || "");
              bookingParams.append("amount", bookingInfo.totalAmount || bookingInfo.amount || "");
              bookingParams.append("distance", bookingInfo.distance || "");

              navigate(`/booking/${primaryBookingId}?${bookingParams.toString()}`);
            }, 3000);

            // Update payment status based on booking ID type
            try {
              // Get the booking ID from the response
              const bookingId =
                updatedBooking.bookingId ||
                updatedBooking.id ||
                updatedBooking._id ||
                updatedBooking.dbId ||
                "";

              // Determine ID type (local storage, custom ID, or MongoDB ID)
              const isLocalId =
                bookingId && bookingId.toString().includes("local-");
              const isCustomId =
                bookingId &&
                (bookingId.toString().startsWith("CB") ||
                  bookingId.toString().includes("local-"));
              // MongoDB ObjectIDs are 24-character hex strings - use standard endpoint for these

              if (isLocalId) {
                // Handle local storage booking update

                // Get existing bookings from localStorage
                const localBookings = localStorage.getItem("bookings")
                  ? JSON.parse(localStorage.getItem("bookings"))
                  : [];

                // Find and update the specific booking
                const updatedLocalBookings = localBookings.map((booking) => {
                  if (booking._id === bookingId) {
                    return {
                      ...booking,
                      paymentId: response.razorpay_payment_id,
                      paymentStatus: "completed",
                      paymentMethod: "online",
                      status: "confirmed",
                      updatedAt: new Date().toISOString(),
                    };
                  }
                  return booking;
                });

                // Save updated bookings back to localStorage
                localStorage.setItem(
                  "bookings",
                  JSON.stringify(updatedLocalBookings),
                );
                toast.success("Payment recorded successfully (offline mode)");
              } else if (bookingId) {
                // This is a MongoDB ID, update in the database

                try {
                  // Prepare payment update data
                  const paymentData = {
                    paymentId: response.razorpay_payment_id,
                    paymentStatus: "completed",
                    paymentMethod:
                      selectedPaymentMethod === "razorpay"
                        ? "ONLINE"
                        : selectedPaymentMethod.toUpperCase(), // Map razorpay to ONLINE
                    // Add custom ID for better server-side handling
                    customBookingId: isCustomId ? bookingId : undefined,
                  };

                  // Determine the appropriate approach based on ID type
                  let paymentUpdateResponse;

                  if (isCustomId) {
                    // For custom IDs (with CB prefix), use the dedicated endpoint
                    paymentUpdateResponse = await axios.post(
                      `${API_URL}/booking/payment-update/custom`,
                      {
                        // Put all data in body for the custom endpoint
                        bookingId: bookingId,
                        paymentId: response.razorpay_payment_id,
                        paymentStatus: "completed",
                        paymentMethod:
                          selectedPaymentMethod === "razorpay"
                            ? "ONLINE"
                            : selectedPaymentMethod.toUpperCase(), // Map razorpay to ONLINE
                      },
                      {
                        headers: {
                          "Content-Type": "application/json",
                        },
                      },
                    );
                  } else {
                    // For MongoDB IDs, use the standard endpoint
                    paymentUpdateResponse = await axios.put(
                      `${API_URL}/booking/${bookingId}/payment-method`,
                      paymentData,
                      config, // Include authentication headers
                    );
                  }

                  if (
                    paymentUpdateResponse.data &&
                    paymentUpdateResponse.data.success
                  ) {
                    toast.success("Payment information saved to database");

                    // Get the updated booking data from server
                    if (paymentUpdateResponse.data.data) {
                      const updatedBookingFromServer =
                        paymentUpdateResponse.data.data;

                      // Update booking data
                      updatedBooking = {
                        ...updatedBooking,
                        status: updatedBookingFromServer.status || "confirmed",
                        paymentStatus:
                          updatedBookingFromServer.paymentStatus || "completed",
                      };
                    }
                  } else {
                    toast.warning(
                      "Payment successful but database update failed",
                    );
                  }
                } catch (apiError) {
                  console.error("API error updating payment:", apiError);
                  toast.warning(
                    "Payment recorded but server update failed - will sync later",
                  );
                }
              } else {
                // No valid ID found
                toast.info("Payment recorded but booking ID not found");
              }

              // Only navigate once - use the proper ID from the database if available
              // Give the browser a moment to process the navigation
              setTimeout(() => {
                const confirmationId =
                  updatedBooking.id || updatedBooking._id || "pending";
                const params = new URLSearchParams({
                  paymentStatus: "success",
                  paymentMethod:
                    selectedPaymentMethod === "razorpay"
                      ? "ONLINE"
                      : selectedPaymentMethod.toUpperCase(), // Map razorpay to ONLINE
                  from: bookingInfo.fromCity,
                  to: bookingInfo.toCity,
                  cabName: bookingInfo.cabName,
                  amount: bookingInfo.totalAmount,
                  date: bookingInfo.pickupDate,
                  time: bookingInfo.pickupTime,
                  distance: bookingInfo.distance,
                  pickupAddress: encodeURIComponent(
                    bookingInfo.pickupAddress || "",
                  ),
                  dropAddress: encodeURIComponent(
                    bookingInfo.dropAddress || "",
                  ),
                });

                // Add service-specific parameters
                if (serviceType === "rental") {
                  params.append("service", "rental");
                  if (queryParams.get("package")) {
                    params.append("packageName", queryParams.get("package"));
                  }
                  if (queryParams.get("city")) {
                    params.append("city", queryParams.get("city"));
                  }
                } else if (serviceType === "airport") {
                  params.append("service", "airport");
                  if (queryParams.get("airport")) {
                    params.append("airport", queryParams.get("airport"));
                  }
                  if (queryParams.get("direction")) {
                    params.append("direction", queryParams.get("direction"));
                  }
                }
                navigate(`/booking/${confirmationId}?${params.toString()}`);
              }, 500);
            } catch (paymentUpdateError) {
              console.error(
                "Error in payment update process:",
                paymentUpdateError,
              );
              toast.error(
                "Payment was successful, but we had trouble updating your booking",
              );

              // Still redirect to booking details, even if there was an error updating the payment status
              setTimeout(() => {
                const params = new URLSearchParams({
                  from: bookingInfo.fromCity,
                  to: bookingInfo.toCity,
                  pickupAddress: bookingInfo.pickupAddress, // Detailed pickup address
                  dropAddress: bookingInfo.dropAddress, // Detailed drop address
                  fromParam: bookingInfo.fromCity, // City for pickup
                  toParam: bookingInfo.toCity, // City for drop-off
                });
                navigate(`/booking/${bookingData.id}?${params.toString()}`);
              }, 500);
            }
          } catch (error) {
            console.error("Error processing payment success:", error);
            toast.error("There was a problem processing your payment");

            // Still navigate somewhere even if processing failed
            setTimeout(() => {
              navigate(`/booking/status?error=true`);
            }, 500);
          }
        },
        prefill: {
          name: bookingInfo.name,
          email: bookingInfo.email || "",
          contact: bookingInfo.mobile,
        },
        notes: {
          bookingId: tempBookingId,
          fromCity: bookingInfo.fromCity,
          toCity: bookingInfo.toCity,
        },
        theme: {
          color: "#3B82F6",
        },
        modal: {
          ondismiss: function () {
            setProcessingPayment(false);
            toast.info("Payment cancelled");
          },
        },
      };

      // Fallback approach in case Razorpay integration fails
      try {
        // Create and open Razorpay instance
        const razorpayInstance = new window.Razorpay(options);
        razorpayInstance.on("payment.failed", function (response) {
          toast.error(
            "Payment failed: " +
              (response.error?.description || "Unknown error"),
          );
          setProcessingPayment(false);
        });
        razorpayInstance.open();
      } catch (razorpayError) {
        console.error("Razorpay error:", razorpayError);

        // Fallback for demo purposes - simulate payment success
        toast.success("Demo mode: Payment simulated successfully");

        // Show payment success animation even in demo mode
        setShowPaymentSuccessAnimation(true);

        // Wait before redirecting
        setTimeout(() => {
          // Redirect directly to booking details with complete trip information
          // Build the redirect URL with proper parameters
          const redirectParams = new URLSearchParams({
            paymentStatus: "success",
            paymentMethod: "online",
            from: bookingInfo.fromCity,
            to: bookingInfo.toCity,
            cabName: bookingInfo.cabName,
            amount: bookingInfo.totalAmount,
            date: bookingInfo.pickupDate,
            time: bookingInfo.pickupTime,
          });

          // Add rental package parameters if this is a rental service
          if (serviceType === "rental") {
            redirectParams.append("service", "rental");
            if (queryParams.get("package")) {
              redirectParams.append("packageName", queryParams.get("package"));
            }
            if (queryParams.get("city")) {
              redirectParams.append("city", queryParams.get("city"));
            }
          }

          // Add via cities if present
          if (queryParams.get("via")) {
            redirectParams.append("via", queryParams.get("via"));
          }

          navigate(`/booking/${bookingData.id}?${redirectParams.toString()}`);
        }, 3000);
      }
    } catch (error) {
      console.error("Payment initiation error:", error);
      toast.error(error.message || "Failed to initiate payment");
      setProcessingPayment(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 flex justify-center items-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Payment Animation Components */}
      {showPaymentSuccessAnimation && (
        <PaymentSuccessAnimation
          paymentMethod={selectedPaymentMethod === "cod" ? "cod" : "online"}
          onComplete={() => {
            setShowPaymentSuccessAnimation(false);
            setShowBookingConfirmedAnimation(true);

            // After showing booking confirmation, redirect to booking details
            setTimeout(() => {
              setShowBookingConfirmedAnimation(false);
            }, 3000);
          }}
        />
      )}

      {showBookingConfirmedAnimation && (
        <BookingConfirmedAnimation
          bookingData={{
            id: bookingInfo._id || bookingInfo.id,
            fromCity: bookingInfo.fromCity,
            toCity: bookingInfo.toCity,
            cabName: bookingInfo.cabName,
            amount: bookingInfo.totalAmount,
            date: bookingInfo.pickupDate,
            time: bookingInfo.pickupTime,
          }}
          onComplete={() => {
            setShowBookingConfirmedAnimation(false);
          }}
        />
      )}

      {/* Animation Styles */}
      <AnimationStyles />

      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Payment</h1>
          <p className="text-gray-600">Secure payment for your cab booking</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 mb-6 lg:mb-8">
          <div className="md:col-span-2 lg:col-span-3">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-bold mb-4">Apply Promo Code</h2>

              <div className="flex items-stretch">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Enter promo code"
                  disabled={promoApplied}
                  className={`flex-1 border rounded-l-lg p-3 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    promoApplied ? "bg-gray-100" : ""
                  }`}
                />
                {promoApplied ? (
                  <button
                    onClick={removePromoCode}
                    className="bg-gray-200 px-4 rounded-r-lg flex items-center justify-center hover:bg-gray-300 transition-colors"
                  >
                    <FaTimes className="text-gray-600" />
                  </button>
                ) : (
                  <button
                    onClick={applyPromoCode}
                    className="bg-blue-600 text-white px-4 py-3 rounded-r-lg font-medium hover:bg-blue-700 transition-colors"
                  >
                    Apply
                  </button>
                )}
              </div>

              {promoApplied && (
                <div className="mt-2 text-green-600 flex items-center">
                  <FaTag className="mr-2" />
                  <span>Promo code applied! You saved ₹{promoDiscount}</span>
                </div>
              )}

              <div className="mt-4 text-sm text-gray-500">
                <p>Available promo codes: {availableCoupons.map(c => c.code).join(', ')}</p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold mb-4">Select Payment Method</h2>

              <div className="space-y-4">
                <div
                  className={`border rounded-xl p-5 cursor-pointer transition-all shadow-sm hover:shadow-md ${
                    selectedPaymentMethod === "razorpay"
                      ? "border-blue-500 bg-gradient-to-r from-blue-50 to-blue-100"
                      : "hover:border-gray-300"
                  }`}
                  onClick={() => setSelectedPaymentMethod("razorpay")}
                >
                  <div className="flex items-center">
                    <div
                      className={`h-6 w-6 rounded-full border-2 ${selectedPaymentMethod === "razorpay" ? "border-blue-500 bg-white" : "border-gray-400"} flex items-center justify-center mr-3 transition-all`}
                    >
                      {selectedPaymentMethod === "razorpay" && (
                        <div className="h-3 w-3 rounded-full bg-blue-500 animate-pulse"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center">
                        <img
                          src="https://razorpay.com/favicon.png"
                          alt="Razorpay Logo"
                          className="w-6 h-6 mr-2"
                        />
                        <span className="font-semibold text-lg">Razorpay</span>
                      </div>
                      <p className="text-gray-500 text-sm mt-1 ml-6">
                        Pay securely with cards, UPI, wallets & more
                      </p>
                    </div>
                  </div>
                  {selectedPaymentMethod === "razorpay" && (
                    <div className="mt-4 ml-9 text-sm text-gray-600">
                      <p>• All payment methods accepted in one place</p>
                      <p>• Fast and secure payment process</p>
                      <p>• No additional charges</p>
                    </div>
                  )}
                </div>

                <div
                  className={`border rounded-xl p-5 cursor-pointer transition-all shadow-sm hover:shadow-md ${
                    selectedPaymentMethod === "cod"
                      ? "border-green-500 bg-gradient-to-r from-green-50 to-green-100"
                      : "hover:border-gray-300"
                  }`}
                  onClick={() => setSelectedPaymentMethod("cod")}
                >
                  <div className="flex items-center">
                    <div
                      className={`h-6 w-6 rounded-full border-2 ${selectedPaymentMethod === "cod" ? "border-green-500 bg-white" : "border-gray-400"} flex items-center justify-center mr-3 transition-all`}
                    >
                      {selectedPaymentMethod === "cod" && (
                        <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-green-600 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                        <span className="font-semibold text-lg">
                          Cash on Delivery
                        </span>
                      </div>
                      <p className="text-gray-500 text-sm mt-1 ml-6">
                        Pay directly to driver when you reach destination
                      </p>
                    </div>
                  </div>
                  {selectedPaymentMethod === "cod" && (
                    <div className="mt-4 ml-9 text-sm text-gray-600">
                      <p>• Pay when you reach your destination</p>
                      <p>• No online payment required</p>
                      <p>• Cash, UPI, and cards accepted on delivery</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-8">
                <button
                  onClick={initiatePayment}
                  disabled={processingPayment}
                  className={`w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 px-6 rounded-xl font-semibold text-lg shadow-md hover:shadow-lg hover:from-blue-700 hover:to-blue-800 transform hover:-translate-y-0.5 transition-all duration-300 ${
                    processingPayment ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  {processingPayment ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                      <span>Processing...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <span className="mr-2">
                        {selectedPaymentMethod === "cod"
                          ? "Book with Cash on Delivery"
                          : `Pay ₹${bookingInfo.totalAmount} with Razorpay`}
                      </span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 8l4 4m0 0l-4 4m4-4H3"
                        />
                      </svg>
                    </div>
                  )}
                </button>
                <div className="mt-4 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-green-500 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  <p className="text-center text-sm text-gray-600">
                    Secure payment processed with 256-bit encryption
                  </p>
                </div>
                <p className="text-center text-xs text-gray-500 mt-2">
                  By clicking "Pay", you agree to our{" "}
                  <span className="text-blue-600 cursor-pointer">
                    terms and conditions
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className="md:col-span-1 lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-4">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-3">
                Booking Summary
              </h2>

              {/* Cab Details Section */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-6 border border-blue-200">
                <div className="text-center">
                  {bookingInfo.cabImage && (
                    <div className="mb-4">
                      <img
                        src={bookingInfo.cabImage}
                        alt={bookingInfo.cabName}
                        className="mx-auto h-20 w-32 object-cover rounded-lg shadow-md"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  )}
                  <h3 className="font-bold text-xl text-blue-800 mb-2">
                    {bookingInfo.cabName}
                  </h3>
                  <p className="text-blue-600 font-medium">
                    {bookingInfo.cabName}
                  </p>
                </div>
              </div>

              {/* Trip Details Section */}
              <div className="space-y-3 mb-6">
                {/* Route - Only show for non-rental services */}
                {serviceType !== "rental" && (
                  <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-blue-500">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Route:</span>
                      <span className="font-semibold text-gray-800">
                        {bookingInfo.fromCity} → {bookingInfo.toCity}
                      </span>
                    </div>
                  </div>
                )}

                {/* Package Name for Rental Services */}
                {serviceType === "rental" && packageName && (
                  <div className="bg-green-50 rounded-lg p-3 border-l-4 border-green-500">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Package:</span>
                      <span className="font-semibold text-green-800">
                        {decodeURIComponent(packageName)}
                      </span>
                    </div>
                  </div>
                )}

                {/* City for Rental Services */}
                {serviceType === "rental" && (
                  <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-purple-500">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">City:</span>
                      <span className="font-semibold text-gray-800">
                        {queryParams.get("city") || bookingInfo.fromCity}
                      </span>
                    </div>
                  </div>
                )}

                {/* Pickup Address */}
                {bookingInfo.pickupAddress && (
                  <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-green-500">
                    <div className="flex justify-between items-start">
                      <span className="text-gray-700 font-medium">
                        Pickup Address:
                      </span>
                      <span className="font-semibold text-gray-800 text-right max-w-xs">
                        {bookingInfo.pickupAddress}
                      </span>
                    </div>
                  </div>
                )}

                {/* Drop Address */}
                {bookingInfo.dropAddress && (
                  <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-red-500">
                    <div className="flex justify-between items-start">
                      <span className="text-gray-700 font-medium">
                        Drop Address:
                      </span>
                      <span className="font-semibold text-gray-800 text-right max-w-xs">
                        {bookingInfo.dropAddress}
                      </span>
                    </div>
                  </div>
                )}

                {/* Date */}
                {bookingInfo.travelDate && (
                  <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-purple-500">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Date:</span>
                      <span className="font-semibold text-gray-800">
                        {formatDateDDMMYYYY(bookingInfo.travelDate)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Time */}
                {bookingInfo.travelTime && (
                  <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-orange-500">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Time:</span>
                      <span className="font-semibold text-gray-800">
                        {convertTo12HourFormat(bookingInfo.travelTime)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Distance - Hide for rental services */}
                {bookingInfo.distance > 0 && serviceType !== "rental" && (
                  <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-indigo-500">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">
                        Distance:
                      </span>
                      <span className="font-semibold text-gray-800">
                        {Math.round(bookingInfo.distance)} km
                      </span>
                    </div>
                  </div>
                )}

                {/* Trip Type - Show for all services */}
                <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-teal-500">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">
                      Trip Type:
                    </span>
                    <span className="font-semibold text-gray-800">
                      {serviceType === "rental"
                        ? "Local Rental"
                        : serviceType === "airport"
                        ? "Airport Transfer"
                        : bookingInfo.bookingType === "roundTrip"
                        ? "Round Trip"
                        : "One Way"}
                    </span>
                  </div>
                </div>

                {/* Airport Direction - Show for airport services */}
                {serviceType === "airport" && queryParams.get("direction") && (
                  <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-blue-500">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">
                        Direction:
                      </span>
                      <span className="font-semibold text-gray-800">
                        {queryParams.get("direction") === "to" ? "To Airport" : "From Airport"}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Price Breakdown Section */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6 border border-gray-200">
                <h4 className="font-bold text-lg mb-4 text-gray-800 border-b pb-2">
                  Price Breakdown
                </h4>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3 border-l-4 border-blue-500">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">
                        Actual Price:
                      </span>
                      <span className="font-bold text-lg text-gray-800">
                        ₹{bookingInfo.amount?.toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                  {promoApplied && (
                    <div className="bg-green-50 rounded-lg p-3 border-l-4 border-green-500">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-green-700">
                          Promo Discount:
                        </span>
                        <span className="font-bold text-lg text-green-600">
                          -₹{promoDiscount}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="bg-white rounded-lg p-3 border-l-4 border-orange-500">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">
                        GST (18%): {/* Updated for all services including rental packages */}
                      </span>
                      <span className="font-bold text-lg text-gray-800">
                        ₹{bookingInfo.taxAmount?.toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 rounded-lg text-white border-l-4 border-blue-700">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-lg">Total Amount:</span>
                      <span className="font-bold text-2xl">
                        ₹{bookingInfo.totalAmount?.toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Details Section */}
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <h3 className="font-bold text-lg mb-4 text-gray-800 border-b pb-2">
                  Customer Details
                </h3>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3 border-l-4 border-green-500">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Name:</span>
                      <span className="font-bold text-gray-800">
                        {bookingInfo.name}
                      </span>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border-l-4 border-blue-500">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Mobile:</span>
                      <span className="font-bold text-gray-800">
                        +91 {bookingInfo.mobile}
                      </span>
                    </div>
                  </div>
                  {bookingInfo.email && (
                    <div className="bg-white rounded-lg p-3 border-l-4 border-purple-500">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">
                          Email:
                        </span>
                        <span className="font-bold text-gray-800">
                          {bookingInfo.email}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
